// src/lib/script-levels.ts
import {
  LEVEL_1_CODE,
  LEVEL_2_CODE,
  LEVEL_3_CODE,
  LEVEL_4_CODE,
  LEVEL_5_CODE,
  LEVEL_6_CODE,
  LEVEL_7_CODE,
  LEVEL_8_CODE,
  LEVEL_9_CODE,
  LEVEL_10_CODE,
} from "./script-code";

export const levels = [
  {
    id: 1,
    title: "Level 1: Let's Start Simple – Copy & Paste",
    description:
      "Begin by running a basic script that logs a simple message. This will help you see how Google Ads Scripts work.",
    preCodeTasks: [
      "Open Google Ads & click on Tools in the left menu.",
      "Click on Bulk Actions, then Scripts.",
      "Click the big blue '+' button & then 'New Script'.",
      "It should look like the image below with a 3-line script already in it.",
      "Click the button below the image to copy a new script to the clipboard (you can see the code we'll be using below).",
    ],
    postCodeTasks: [
      "Name your script 'Level 1'.",

      "Delete the 3 lines of placeholder code from Google",
      "Paste in the code we just saved to your clipboard.",
      "Click 'Authorise' to give the script permission to run.",
      "Click 'Preview' (at the bottom) to get the script running in preview mode.",
      "(You might need to 'Authorize' again - you may need to scroll down to see it).",
      "If you see an 'App Blocked' message, talk to your Google admin.",
      "Once script is 'Done', check the output of your script by clicking 'Logs'.",
    ],
    code: LEVEL_1_CODE,
    expectedOutput: "Hello, Google Ads Scripts!",
    images: "public/images/logs.png",
  },
  {
    id: 2,
    title: "Level 2: Find Your Account Info",
    description:
      "Learn how to pull basic details about your account, like its name and ID. A quick way to see what's available.",
    preCodeTasks: [
      "Click the button below to copy this new script to the clipboard.",
    ],
    postCodeTasks: [
      "In the script editing window, delete the code from Level 1.",
      "Paste this new code into the script window.",
      "You should now see the 5 lines of code below.",
      "Click 'Preview' to run the script, then check the logs.",
    ],
    code: LEVEL_2_CODE,
    expectedOutput: [
      "Account Name: [Your Account Name]",
      "Account ID: [Your Account ID]",
    ],
    images: "public/images/level2.png",
  },
  {
    id: 3,
    title: "Level 3: Check Your Key Stats",
    description:
      "Discover how to pull numbers like impressions, clicks, and CTR. These are the building blocks for analyzing performance.",
    preCodeTasks: [
      "You know what to do...Click the button below to copy this script to the clipboard.",
      "Paste your script into Google Ads (delete the old code first).",
    ],
    postCodeTasks: [
      "This time let's click 'Run' instead of 'Preview'.",
      "A lightbox will appear, click 'Run without Preview'.",
      "You'll end up on the 'Script History' page. Click '3 log statements' to see the logs of your script run.",
      "To get back to your script, click 'Scripts' at the top.",
      "Scripts is where you can see all your scripts & schedule a script to run on a regular basis (look for 'Frequency').",
      "To get back to editing your script for the next level, just click on the script name.",
    ],
    code: LEVEL_3_CODE,
    expectedOutput: [
      "Impressions: [Number]",
      "Clicks: [Number]",
      "CTR: [Percentage]",
    ],
    images: "public/images/history.png",
  },
  {
    id: 4,
    title: "Level 4: Choose Your Date Range",
    description:
      "Change the date range in your script to see data for different time periods, \nlike yesterday or the last 7 days. While we're at it, let's add some more metrics!",
    preCodeTasks: [
      "Copy the code.",
      "Paste it into Google Ads.",
      "If still on the 'Details' page you'll need to click the script name to go back to editing.",
    ],
    postCodeTasks: ["Click 'Preview'."],
    code: LEVEL_4_CODE,
    expectedOutput: [
      "Impressions: [Number]",
      "Clicks: [Number]",
      "CTR: [Percentage]",
      "Cost: [Currency]",
      "Conv: [Number]",
    ],
  },
  {
    id: 5,
    title: "Level 5: Save Data to Google Sheets",
    description: `Logging metrics on the screen can be useful, but it's even better to save them to a Google Sheet.
So let's do that! This time a Google Sheet will be created for you - remember to check the logs to get the link.`,
    preCodeTasks: ["Copy the code.", "Paste it into Google Ads."],
    postCodeTasks: [
      "Click 'Preview'.",
      "This time you'll need to Authorize the script. Click the Authorize button, Google login & then 'Allow'.",
      "Click 'Preview' again to run the script.",
      "Find the URL of your sheet by looking at the logs.",
      "Open your sheet in a new tab & explore the data.",
    ],
    code: LEVEL_5_CODE,
    expectedOutput: ["Sheet URL: [Google Sheet Link]"],
  },
  {
    id: 6,
    title: "Level 6: Organize Data into Tabs",
    description: `Now that you're getting the hang of this, we're going to try a new way of getting data. It's faster but looks a bit scarier.
Let's create 3 tabs with data for campaigns, ad groups, and keywords. This will make it easier to read and report on.\n
After all, the reason to pull this data out is to create reports & charts and gain new insights.
It might take about a minute or two to run.
Want to learn more? Try pasting this code into chatGPT or Claude.ai & ask it to explain what each part does.
NOTE: This code only looks for SEARCH campaigns. But try adjusting it (with AI) to get data for other campaign types.`,
    preCodeTasks: ["Copy the code.", "Paste it into Google Ads."],
    postCodeTasks: [
      "Click 'Preview'.",
      "Once the code is finished, copy the URL into a tab & explore the sheet.",
    ],
    code: LEVEL_6_CODE,
    expectedOutput: [
      "Search campaign report created! URL: [Google Sheet Link]",
    ],
  },
  {
    id: 7,
    title: "Level 7: Making Your Sheet More Useful",
    description: `Now that you've created your first report, you might notice it contains a lot of rows with zeros - campaigns or keywords with no activity.
(Assuming you had some Search campaigns & keywords in your account).
Let's clean up our data by filtering out these inactive items. This will make your report:
• Easier to read
• Faster to generate
• More focused on what matters
We'll also format the numbers to make them more readable.`,
    preCodeTasks: [
      "Copy the code below.",
      "Create a new script in Google Ads (or edit your existing one).",
      "With a bit of help from AI, you could try adjusting the filters in the code to match your needs.",
    ],
    postCodeTasks: [
      "Click 'Preview' to run the script.",
      "Compare this sheet to your previous one - notice how much cleaner (& faster) it is!",
      "Try adjusting the filters (like adding a rule to only get entities with 100 impressions or more) to see how it affects your data.",
    ],
    code: LEVEL_7_CODE,
    expectedOutput: ["Filtered report created! URL: [Google Sheet Link]"],
  },
  {
    id: 8,
    title: "Level 8: Using the same sheet every time",
    description: `So far we've been creating a new sheet every time the script runs. While this is useful for testing, in practice you'll want to update the same sheet with new data, not create new sheets each time.
Let's modify our script to use the same sheet everytime. This way you can: add your own formatting and charts, track changes over time, share the sheet with your team & schedule the script to auto-update your sheet
`,
    preCodeTasks: [
      "Copy the complete code",
      "Paste it into Google Ads (or edit your existing script)",
    ],
    postCodeTasks: [
      "IMPORTANT - Use the same Google account for the sheet that you use for Google Ads",
      "Open a new browser tab",
      "Type 'sheet.new' in address bar (and press enter) to create a new, blank Google Sheet",
      "⚠️ Copy the entire URL from your browser's address bar - don't try to 'tidy it up' :)",
      "Find row 3 of the script, the one that says   const SHEET_URL = '';",
      "Enter the url of your new sheet between the single quotes",
      "Click 'Preview' to run the script",
      "Check that data appears in your sheet",
      "Try running the script again - now it updates the same sheet instead of creating a new one",
    ],
    code: LEVEL_8_CODE,
    expectedOutput: ["Data updated in sheet: [Your Google Sheet URL]"],
    images: "images/sheet-url.png",
  },
  {
    id: 9,
    title: "Level 9: Let's automate some email alerts",
    description:
      "Set up automated email alerts when enabled campaigns get zero impressions. \nPerfect for catching issues early, like disapproved ads or incorrect targeting.",
    preCodeTasks: ["Copy the code below & paste it into Google Ads."],
    postCodeTasks: [
      "Add your email address at the top of the script  - line 1 this time.",
      "Add your email address in the YOUR_EMAIL variable (between the single quotes).",
      "Click 'Preview' to test the script.",
      "The script will only email you if there's a problem - no email means everything's running fine!",
      "If you want to test that it works, try setting the condition (line 6 )to 'Impressions > 0' and run the script again.",
      "Note the email might go to spam!",
    ],
    code: LEVEL_9_CODE,
    expectedOutput: ["Alert email sent to [YOUR_EMAIL_ADDRESS]"],
  },
  {
    id: 10,
    title: "Level 10: Optimise your profits with 8020agent",
    description:
      "CONGRATULATIONS! You've completed all the levels. \nNow it's time to use the script in this app to get insights about your budgets & more. \n\nLogin to this app (if you haven't already) & go through the setup process.",
    preCodeTasks: [
      "No code to copy this time - just login to the app & go through the setup process.",
    ],
    postCodeTasks: ["Go celebrate! You're a Google Ads Scripts hero!"],
    code: LEVEL_10_CODE,
    expectedOutput: ["Nothing here... gone to the pub!"],
  },
];
